import {
  DEFAULT_ZOOM_PERCENT,
  DESIGN_WIDTH,
  VIEWPORT_QUERY_PARAM,
  VIEWPORT_QUERY_VALUE,
} from "./constants";

const SIZE_EPSILON = 0.5;

/**
 * The window's width in a coordinate system that **browser page zoom does not change**.
 *
 * `window.outerWidth` is measured in screen pixels, so Ctrl/Cmd ± leaves it alone while
 * `innerWidth` moves. That difference is the whole mechanism here: `innerWidth / outerWidth`
 * *is* the zoom level, exactly, with no estimation and no dead-zone heuristic — so 90% and
 * 110%, which Chrome offers and a ratio-guard would have swallowed as window-chrome noise,
 * register like every other step.
 *
 * ### What it is used for, and why the previous two answers were both wrong
 *
 * `computeDesignViewport` fixes the *scale* from this and derives the logical width from
 * `innerWidth`. Pinning the scale to a zoom-independent width is what makes browser zoom
 * behave the way a user expects on any other site — zoom out, the design shrinks and more of
 * it fits; zoom in, it grows — while the frame still spans the window exactly.
 *
 *   * **Pinning `logicalWidth` to 1600 instead** (until 2026-08-28) made zoom a no-op: zooming
 *     out grew `innerWidth` and shrank `scale` by the same factor, so the user saw the same
 *     1600 design pixels at the same size. That is the "the ratio is staying the same" report.
 *   * **Multiplying the measured size by the zoom ratio** (`bitsonic-portal`'s
 *     `estimateBrowserPageZoom`, and this file until 2026-08-28) held the frame at its
 *     unzoomed *physical* size, so zoom-out grew the window past the frame and `shell.css`'s
 *     `#000` showed through — the black band down the right and bottom.
 *
 * Falls back to `innerWidth` when `outerWidth` is unavailable or nonsensical, which reproduces
 * the old fit-to-width behaviour rather than dividing by something bogus.
 *
 * "Nonsensical" includes an `outerWidth` far *smaller* than `innerWidth`. Browser zoom bottoms
 * out at 25 %, so `outerWidth / innerWidth` cannot legitimately fall below about 0.25; anything
 * under `MIN_PLAUSIBLE_ZOOM` is not zoom but a bad reading. Measured 2026-10-16: an
 * automation-driven window reported `outerWidth` 160 against `innerWidth` 1536, and the whole
 * app rendered at 10 % (`scale` 0.1, a 15,360 px logical width) — unusable, with nothing
 * wrong on the page itself.
 */
const MIN_PLAUSIBLE_ZOOM = 0.2;

export function readReferenceWidth() {
  const { outerWidth, innerWidth } = window;
  if (!outerWidth || !innerWidth) return Math.max(1, innerWidth || 1);
  if (outerWidth < innerWidth * MIN_PLAUSIBLE_ZOOM) return Math.max(1, innerWidth);
  return Math.max(1, outerWidth);
}

/** True while the user is pinch-zooming (iOS/trackpad visual viewport). */
export function isPinchZooming() {
  const scale = window.visualViewport?.scale;
  return typeof scale === "number" && Math.abs(scale - 1) > 0.01;
}

/**
 * The window the design viewport has to fill, in CSS pixels — i.e. *at the user's current
 * zoom*, which is exactly why this is read raw. Paired with `readReferenceWidth`, the two
 * together tell `computeDesignViewport` both how big the window is and how zoomed it is.
 *
 * Pinch zoom never reaches here: `DesignViewportShell` skips the recompute entirely while
 * `isPinchZooming()` is true, so the browser magnifies a frozen layout.
 */
export function readPhysicalSize() {
  return {
    width: Math.max(1, window.innerWidth),
    height: Math.max(1, window.innerHeight),
  };
}

export function viewportSizeChanged(prev, next, ...__rest: any[]) {
  return (
    !prev ||
    Math.abs(prev.width - next.width) > SIZE_EPSILON ||
    Math.abs(prev.height - next.height) > SIZE_EPSILON ||
    Math.abs(prev.scale - next.scale) > 0.0001
  );
}

/**
 * The design viewport: a frame of `logicalWidth × logicalHeight` CSS pixels, CSS-transformed
 * by `scale` so it covers the window exactly.
 *
 * ```
 *   baseWidth = DESIGN_WIDTH / zoomFactor          1920 / 1.2 = 1600   (the design's own size)
 *   scale     = referenceWidth / baseWidth         fixed by the WINDOW, not by the zoom
 *   width     = physicalWidth  / scale             grows as the user zooms out
 *   height    = physicalHeight / scale
 * ```
 *
 * Two properties fall out of it, and both were requirements:
 *
 * 1. **The frame always fills the window.** `width * scale === physicalWidth` identically, so
 *    the shell's black background is never exposed at any zoom level.
 * 2. **Browser zoom does what a user expects.** `scale` is pinned to a zoom-independent width,
 *    so zooming out cannot shrink it — instead `width` grows, the app lays out at a wider
 *    logical viewport, and text and controls get smaller with more of them on screen. Zooming
 *    in is the mirror image.
 *
 * Resizing the window still behaves as it always did: `referenceWidth` and `physicalWidth`
 * move together, so `width` stays at the design's own size and the layout is fit-to-width.
 *
 * `DEFAULT_ZOOM_PERCENT` is the *baseline* on top of which the browser's zoom then applies —
 * it decides what 100% looks like, and nothing else.
 */
export function computeDesignViewport(
  physicalWidth,
  physicalHeight,
  referenceWidth = physicalWidth,
  designWidth = DESIGN_WIDTH,
  zoomPercent = DEFAULT_ZOOM_PERCENT
, ...__rest: any[]) {
  const width = Math.max(1, physicalWidth);
  const height = Math.max(1, physicalHeight);
  const reference = Math.max(1, referenceWidth);
  const zoomFactor = Math.max(0.01, (100 + zoomPercent) / 100);
  const baseWidth = designWidth / zoomFactor;
  const scale = reference / baseWidth;

  return {
    scale,
    width: width / scale,
    height: height / scale,
    zoomFactor,
  };
}

export function buildAppFrameSrc() {
  const url = new URL(window.location.href);
  url.searchParams.set(VIEWPORT_QUERY_PARAM, VIEWPORT_QUERY_VALUE);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function isDesignViewportApp() {
  // Already framed (e.g. returned from auth inside the shell iframe): render the
  // app, never nest another shell.
  if (window.self !== window.top) {
    return true;
  }
  return (
    new URLSearchParams(window.location.search as any).get(VIEWPORT_QUERY_PARAM) ===
    VIEWPORT_QUERY_VALUE
  );
}
